/*
 * Global drawer controller — loaded via layout/theme.liquid to guarantee it
 * runs on every page regardless of {% javascript %} snippet processing.
 *
 * The {% javascript %} block in drawer.liquid is kept as a belt-and-braces
 * fallback; window.__drawerControllerReady prevents double-initialisation.
 */
(function () {
  if (window.__drawerControllerReady) return;
  window.__drawerControllerReady = true;

  /*
   * Hoist all drawer elements to <body> so they escape any ancestor stacking
   * contexts created by opacity/transform transitions in their section markup
   * (e.g. the hero overlay). Without this, the drawer's z-index is evaluated
   * within the overlay's stacking context rather than at page root, and it
   * paints behind the fixed header regardless of its z-modal value.
   */
  function hoistDrawers() {
    document.querySelectorAll('[data-drawer]').forEach(function (el) {
      if (el.parentNode !== document.body) document.body.appendChild(el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hoistDrawers);
  } else {
    hoistDrawers();
  }

  /*
   * Dormant drawers: see the [data-drawer-dormant] rule in src/css/theme.css. Each
   * drawer renders dormant (its panel's contents skipped for style and layout) and
   * is woken the first time it opens, or for every drawer once the page has loaded
   * and the browser is idle. From then on a drawer behaves exactly as it did before
   * this existed, including its lazy images loading in the background. Drawers the
   * theme editor re-renders after load are woken with their section.
   */
  function wakeDrawer(drawer) {
    if (drawer) drawer.removeAttribute('data-drawer-dormant');
  }
  function wakeAllDrawers() {
    document.querySelectorAll('[data-drawer-dormant]').forEach(wakeDrawer);
  }
  function wakeAllDrawersWhenIdle() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(wakeAllDrawers, { timeout: 3000 });
    } else {
      window.setTimeout(wakeAllDrawers, 1500);
    }
  }
  if (document.readyState === 'complete') {
    wakeAllDrawersWhenIdle();
  } else {
    window.addEventListener('load', wakeAllDrawersWhenIdle, { once: true });
  }
  document.addEventListener('shopify:section:load', wakeAllDrawers);

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var openCount = 0;
  var MENU_MOTION_MS = 450;

  function lockScroll() {
    openCount += 1;
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) document.body.style.overflow = '';
  }

  window.WhittardScrollLock = { lock: lockScroll, unlock: unlockScroll };

  function panelOf(drawer) {
    return drawer.querySelector('[data-drawer-panel]');
  }

  function isMenuDrawer(drawer) {
    return drawer && drawer.hasAttribute('data-menu-drawer');
  }

  function menuPanelInner(drawer) {
    return drawer.querySelector('[data-drawer-panel-inner]');
  }

  /*
   * A drawer body that scrolls but contains nothing focusable cannot be panned by
   * keyboard (WCAG 2.1.1; axe scrollable-region-focusable). The Taste Promise drawer
   * is the clearest case — one focusable element (its close button) sits above a
   * scrolling panel of copy, so a keyboard user can never reach the text below the fold.
   *
   * Applied here rather than in drawer.liquid because Liquid cannot know whether the
   * body will overflow or what the caller put in `content`: an unconditional tabindex
   * would add a dead tab stop before the menu drawer's 148 links. Re-evaluated on every
   * open, so a body whose content changed between opens (mini bag) is re-tested.
   */
  function syncScrollableBody(drawer) {
    var body = drawer.querySelector('[data-drawer-content]');
    if (!body) return;
    var overflows = body.scrollHeight > body.clientHeight + 1;
    var hasFocusable = !!body.querySelector(FOCUSABLE);
    if (overflows && !hasFocusable) {
      body.setAttribute('tabindex', '0');
    } else {
      body.removeAttribute('tabindex');
    }
  }

  function isOpen(drawer) {
    return !drawer.hasAttribute('aria-hidden');
  }

  function onMotionEnd(el, propertyName, fn) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', handler);
      fn();
    }
    function handler(e) {
      if (e.target !== el || e.propertyName !== propertyName) return;
      finish();
    }
    el.addEventListener('transitionend', handler);
    setTimeout(finish, MENU_MOTION_MS);
  }

  function finishCloseDrawer(drawer) {
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    drawer.removeAttribute('data-menu-open');
    drawer.removeAttribute('data-menu-closing');
    drawer.style.opacity = '';
    drawer.style.pointerEvents = '';
    // Back to the z-60 class, so the next open re-derives its layer from scratch.
    drawer.style.zIndex = '';

    var panel = panelOf(drawer);
    if (panel) panel.style.translate = '';

    unlockScroll();

    var trigger = drawer._trigger;
    if (trigger && trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
    drawer._trigger = null;

    document.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id: drawer.id } }));
  }

  function openDrawer(drawer, trigger) {
    if (!drawer || isOpen(drawer)) return;
    wakeDrawer(drawer);
    drawer._trigger = trigger || document.activeElement;

    /*
     * Layered drawers: one opened on top of another (Taste Promise or the gift panel
     * over the mini bag, quick view over the mini bag) has to paint above it. Every
     * drawer carries the same z-60, so with equal z-index the winner is DOM order —
     * and DOM order is not the source order. hoistDrawers only relocates drawers that
     * were nested inside a section wrapper, appending them to the end of <body>, which
     * puts them after any drawer theme.liquid rendered straight into <body> no matter
     * which came first in the template. The Taste Promise drawer opened behind the mini
     * bag for exactly that reason. Elevating by the number of drawers already open
     * removes the dependency on order entirely.
     */
    if (openCount > 0) drawer.style.zIndex = String(60 + openCount);

    if (drawer.parentNode !== document.body) document.body.appendChild(drawer);

    if (trigger && trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    drawer.removeAttribute('inert');
    drawer.removeAttribute('data-menu-closing');

    if (isMenuDrawer(drawer)) {
      // Paint closed (scaleY 0 + opacity 0) while visible, THEN open — avoids the
      // one-frame full-height flash browsers show when visibility flips with the end state.
      drawer.removeAttribute('data-menu-open');
      drawer.removeAttribute('aria-hidden');
      drawer.style.pointerEvents = 'auto';
      void drawer.offsetHeight;
      requestAnimationFrame(function () {
        drawer.setAttribute('data-menu-open', '');
      });
    } else {
      drawer.removeAttribute('aria-hidden');
      drawer.style.opacity = '1';
      drawer.style.pointerEvents = 'auto';
    }

    var panel = panelOf(drawer);
    if (panel) {
      if (!isMenuDrawer(drawer)) panel.style.translate = '0';
      var initialFocusSelector = drawer.getAttribute('data-drawer-initial-focus');
      var initialFocusEl = initialFocusSelector ? panel.querySelector(initialFocusSelector) : null;
      var focusTarget = initialFocusEl || panel.querySelector('[data-drawer-close]');
      if (focusTarget) {
        if (isMenuDrawer(drawer)) {
          var inner = menuPanelInner(drawer);
          if (inner) {
            onMotionEnd(inner, 'opacity', function () {
              focusTarget.focus({ preventScroll: true });
            });
          } else {
            focusTarget.focus({ preventScroll: true });
          }
        } else {
          void focusTarget.offsetHeight;
          focusTarget.focus({ preventScroll: true });
        }
      }
    }

    // After layout, so scrollHeight/clientHeight are real numbers on both the
    // menu-drawer path (which opens behind a rAF) and the sliding-panel path.
    requestAnimationFrame(function () {
      syncScrollableBody(drawer);
    });

    lockScroll();

    // Symmetric with finishCloseDrawer's drawer:close below. Dispatched from
    // this one choke point so it fires regardless of how the drawer was
    // opened — the delegated [data-drawer-open] click, window.__drawerOpen()
    // (e.g. the post-add-to-cart auto-open), or the ?open-drawer= URL redirect.
    document.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id: drawer.id } }));
  }

  function closeDrawer(drawer) {
    if (!drawer || !isOpen(drawer)) return;

    if (isMenuDrawer(drawer)) {
      if (drawer.hasAttribute('data-menu-closing')) return;
      // Fade only (opacity) — no scaleY scroll-up.
      drawer.setAttribute('data-menu-closing', '');
      var inner = menuPanelInner(drawer);
      if (!inner) {
        finishCloseDrawer(drawer);
        return;
      }
      onMotionEnd(inner, 'opacity', function () {
        finishCloseDrawer(drawer);
      });
      return;
    }

    finishCloseDrawer(drawer);
  }

  window.__drawerOpen = function (id, trigger) {
    openDrawer(document.getElementById(id), trigger);
  };

  function topOpenDrawer() {
    var open = document.querySelectorAll('[data-drawer]:not([aria-hidden])');
    return open.length ? open[open.length - 1] : null;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.getAttribute('data-drawer-open')), opener);
      return;
    }

    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('[data-drawer]'));
      return;
    }

    if (event.target.hasAttribute('data-drawer-backdrop')) {
      closeDrawer(event.target.closest('[data-drawer]'));
    }
  });

  /*
   * layout/theme.liquid redirects /cart to /?open-drawer=<id> (a shopper should never
   * land on the bare no-JS cart page - see that file's own comment). Read on every page
   * load, not just the homepage the redirect currently targets, so the mechanism keeps
   * working if the redirect target ever changes. openDrawer no-ops safely if the id does
   * not resolve to a real drawer, the same guard the click-delegate below already relies
   * on. The param is stripped via replaceState so refreshing does not reopen the drawer
   * and the address bar does not carry an implementation detail.
   */
  var openDrawerParam = new URLSearchParams(window.location.search).get('open-drawer');
  if (openDrawerParam) {
    openDrawer(document.getElementById(openDrawerParam));

    var remainingParams = new URLSearchParams(window.location.search);
    remainingParams.delete('open-drawer');
    var remainingQuery = remainingParams.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (remainingQuery ? '?' + remainingQuery : '') + window.location.hash
    );
  }

  document.addEventListener('keydown', function (event) {
    var drawer = topOpenDrawer();
    if (!drawer) return;

    if (event.key === 'Escape') {
      closeDrawer(drawer);
      return;
    }

    if (event.key === 'Tab') {
      var panel = panelOf(drawer);
      if (!panel) return;
      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  });
})();
